import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { message } from "antd";
import { LoginCredentials, AuthState, User } from "@/types";
import { authService } from "@/services/authService";
import { getRolesFromMsloginToken } from "@/utils/jwtMslogin";

/** 与登录接口一致，写入 localStorage，供 msAdmin/ServiceContext 等读取最新角色与 rolePermissions */
function persistMsloginUser(u: User): void {
  try {
    const fromJwt = getRolesFromMsloginToken();
    const mergedRoles = [
      ...new Set([...(u.roles ?? []), ...fromJwt]),
    ];
    localStorage.setItem(
      "mslogin_user",
      JSON.stringify({
        id: u.id,
        account: u.username,
        name: u.name,
        description: u.description ?? "",
        roles: mergedRoles,
        roleDetails: u.roleDetails ?? [],
        rolePermissions: u.rolePermissions ?? [],
        permissions: u.permissions ?? [],
        isSuperAdmin: u.isSuperAdmin === true,
      }),
    );
  } catch {
    /* ignore quota / private mode */
  }
}

/** 将登录/验票/刷新返回的字段统一为前端 User（多角色、按角色权限列表） */
function mapToAuthUser(raw: {
  id?: string;
  adminId?: string;
  account?: string;
  username?: string;
  name?: string;
  description?: string;
  roles?: string[];
  permissions?: string[];
  rolePermissions?: { roleCode: string; permissions: string[] }[];
  roleDetails?: { code: string; name?: string; isActive?: boolean }[];
  isSuperAdmin?: boolean;
  is_super_admin?: boolean;
}): User {
  const id = String(raw.id ?? raw.adminId ?? "");
  const isSuper =
    raw.isSuperAdmin === true || raw.is_super_admin === true;
  const rolesFromApi = Array.isArray(raw.roles) ? [...raw.roles] : [];
  const roles = [
    ...new Set([...rolesFromApi, ...getRolesFromMsloginToken()]),
  ];
  const rolePermissions = Array.isArray(raw.rolePermissions)
    ? raw.rolePermissions.map((r) => ({
        roleCode: r.roleCode,
        permissions: [...r.permissions],
      }))
    : [];
  const permissions = Array.isArray(raw.permissions) ? raw.permissions : [];
  const roleDetails = Array.isArray(raw.roleDetails)
    ? raw.roleDetails
        .filter(
          (d) =>
            d &&
            typeof d.code === "string" &&
            String(d.code).trim().length > 0,
        )
        .map((d) => ({
          code: String(d.code).trim(),
          name: typeof d.name === "string" ? d.name : "",
          isActive: d.isActive !== false,
        }))
    : undefined;
  const account = raw.account || raw.username || raw.name || "";
  return {
    id,
    userId: id,
    username: account,
    email: account,
    name: account,
    description: raw.description || "",
    avatar: undefined,
    role: isSuper ? "super_admin" : roles.length ? roles.join("、") : "admin",
    roles,
    roleDetails,
    rolePermissions,
    isSuperAdmin: isSuper,
    isActive: true,
    lastLoginAt: new Date().toISOString(),
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    permissions,
  };
}

interface AuthContextType extends AuthState {
  login: (credentials: LoginCredentials) => Promise<boolean>;
  /** 企微扫码：OAuth 回调中的 code */
  loginWithWecomCode: (code: string) => Promise<boolean>;
  logout: () => void;
  refreshToken: () => Promise<boolean>;
  checkPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  hasAllPermissions: (permissions: string[]) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: false,
    isLoading: true,
    loading: true,
    user: null,
    permissions: [],
    token: null,
  });

  // 初始化认证状态
  useEffect(() => {
    initializeAuth();
  }, []);

  const initializeAuth = async () => {
    try {
      const token = localStorage.getItem("mslogin_token");
      if (!token) {
        setAuthState((prev) => ({ ...prev, isLoading: false, loading: false }));
        return;
      }

      // 先设置一个临时的认证状态，避免立即跳转到登录页
      setAuthState((prev) => ({
        ...prev,
        isAuthenticated: true, // 临时设置为已认证
        isLoading: true,
        loading: true,
      }));

      // 调用后端接口验证token，增加重试机制
      let result = null;
      let retryCount = 0;
      const maxRetries = 3;

      while (retryCount < maxRetries) {
        try {
          result = await authService.verifyToken(token);
          break; // 成功则跳出循环
        } catch (error) {
          retryCount++;
          if (retryCount < maxRetries) {
            await new Promise((resolve) => setTimeout(resolve, 1000)); // 等待1秒后重试
          }
        }
      }

      if (result?.success && result.data?.payload) {
        const payload = result.data.payload;
        const permissions = payload.permissions || [];

        const verifiedUser = mapToAuthUser({
          id: payload.adminId,
          adminId: payload.adminId,
          account: payload.account,
          username: payload.username,
          roles: payload.roles,
          roleDetails: payload.roleDetails,
          rolePermissions: payload.rolePermissions,
          permissions,
          isSuperAdmin: payload.isSuperAdmin ?? payload.is_super_admin,
        });
        persistMsloginUser(verifiedUser);
        setAuthState({
          isAuthenticated: true,
          isLoading: false,
          loading: false,
          user: verifiedUser,
          permissions,
          token,
        });

        window.dispatchEvent(new CustomEvent("userPermissionsChanged"));
      } else {
        // token验证失败，尝试刷新token
        const refreshSuccess = await refreshToken();
        if (!refreshSuccess) {
          // 刷新也失败，检查本地存储的用户信息
          const localUser = localStorage.getItem("mslogin_user");
          if (localUser) {
            try {
              const userData = JSON.parse(localUser);
              // 如果是超级管理员或其他有权限的用户，允许访问欢迎页面
              if (
                userData.roles &&
                (userData.roles.includes("super_admin") ||
                  userData.roles.includes("admin"))
              ) {
                setAuthState({
                  isAuthenticated: true,
                  isLoading: false,
                  loading: false,
                  user: mapToAuthUser({
                    id: userData.id,
                    account: userData.account,
                    username: userData.name,
                    description: userData.description,
                    roles: userData.roles,
                    roleDetails: userData.roleDetails,
                    rolePermissions: userData.rolePermissions,
                    permissions: userData.permissions,
                    isSuperAdmin: userData.isSuperAdmin,
                  }),
                  permissions: userData.permissions || [],
                  token,
                });
                return; // 直接返回，不执行后面的清除逻辑
              }
            } catch (error) {
              // 解析本地用户信息失败
            }
          }

          // 如果没有有效的本地用户信息，清除本地存储
          localStorage.removeItem("mslogin_token");
          localStorage.removeItem("mslogin_user");
          localStorage.removeItem("mslogin_refresh_token");
          setAuthState((prev) => ({
            ...prev,
            isAuthenticated: false,
            isLoading: false,
            loading: false,
          }));
        }
      }
    } catch (error) {
      // 网络错误或其他错误，尝试刷新token
      const refreshSuccess = await refreshToken();
      if (!refreshSuccess) {
        // 刷新也失败，检查本地存储的用户信息
        const localUser = localStorage.getItem("mslogin_user");
        if (localUser) {
          try {
            const userData = JSON.parse(localUser);
            // 如果是超级管理员或其他有权限的用户，允许访问欢迎页面
            if (
              userData.roles &&
              (userData.roles.includes("super_admin") ||
                userData.roles.includes("admin"))
            ) {
              const token = localStorage.getItem("mslogin_token");
              setAuthState({
                isAuthenticated: true,
                isLoading: false,
                loading: false,
                user: mapToAuthUser({
                  id: userData.id,
                  account: userData.account,
                  username: userData.name,
                  roles: userData.roles,
                  roleDetails: userData.roleDetails,
                  rolePermissions: userData.rolePermissions,
                  permissions: userData.permissions,
                  isSuperAdmin: userData.isSuperAdmin,
                }),
                permissions: userData.permissions || [],
                token,
              });
              return; // 直接返回，不执行后面的清除逻辑
            }
          } catch (error) {
            // 解析本地用户信息失败
          }
        }

        // 如果没有有效的本地用户信息，清除可能无效的token
        localStorage.removeItem("mslogin_token");
        localStorage.removeItem("mslogin_user");
        localStorage.removeItem("mslogin_refresh_token");
        setAuthState((prev) => ({
          ...prev,
          isAuthenticated: false,
          isLoading: false,
          loading: false,
        }));
      }
    }
  };

  // 登录 - 使用后端接口
  const login = async (credentials: LoginCredentials): Promise<boolean> => {
    try {
      setAuthState((prev) => ({ ...prev, isLoading: true }));

      // 调用后端登录接口
      const result = await authService.login(credentials);

      if (result.success && result.data) {
        const { user, accessToken, refreshToken } = result.data as any;

        // 存储到本地
        localStorage.setItem("mslogin_token", accessToken);
        if (refreshToken) {
          localStorage.setItem("mslogin_refresh_token", refreshToken);
        }
        const loggedInUser = mapToAuthUser({
          id: user.id,
          account: user.account,
          username: user.username,
          description: user.description,
          roles: user.roles,
          roleDetails: user.roleDetails,
          rolePermissions: user.rolePermissions,
          permissions: user.permissions,
          isSuperAdmin: user.isSuperAdmin,
        });
        persistMsloginUser(loggedInUser);

        setAuthState({
          isAuthenticated: true,
          isLoading: false,
          loading: false,
          user: loggedInUser,
          permissions: user.permissions || [],
          token: accessToken,
        });

        window.dispatchEvent(new CustomEvent("userPermissionsChanged"));

        message.success("登录成功");
        return true;
      } else {
        message.error(result.message || "登录失败");
        setAuthState((prev) => ({ ...prev, isLoading: false, loading: false }));
        return false;
      }
    } catch (error) {
      message.error("登录失败，请稍后重试");
      setAuthState((prev) => ({ ...prev, isLoading: false, loading: false }));
      return false;
    }
  };

  const loginWithWecomCode = async (code: string): Promise<boolean> => {
    try {
      setAuthState((prev) => ({ ...prev, isLoading: true }));
      const appId = import.meta.env.VITE_MSORG_APPLICATION_ID as string | undefined;
      const result = await authService.wecomLogin({
        code,
        ...(appId?.trim() ? { applicationId: appId.trim() } : {}),
      });

      if (result.success && result.data) {
        const { user, accessToken, refreshToken } = result.data as any;

        localStorage.setItem("mslogin_token", accessToken);
        if (refreshToken) {
          localStorage.setItem("mslogin_refresh_token", refreshToken);
        }
        const wecomUser = mapToAuthUser({
          id: user.id,
          account: user.account,
          username: user.username,
          description: user.description,
          roles: user.roles,
          roleDetails: user.roleDetails,
          rolePermissions: user.rolePermissions,
          permissions: user.permissions,
          isSuperAdmin: user.isSuperAdmin,
        });
        persistMsloginUser(wecomUser);

        setAuthState({
          isAuthenticated: true,
          isLoading: false,
          loading: false,
          user: wecomUser,
          permissions: user.permissions || [],
          token: accessToken,
        });

        window.dispatchEvent(new CustomEvent("userPermissionsChanged"));
        message.success("企微登录成功");
        return true;
      } else {
        message.error(result.message || "企微登录失败");
        setAuthState((prev) => ({ ...prev, isLoading: false, loading: false }));
        return false;
      }
    } catch (error) {
      message.error("企微登录失败，请稍后重试");
      setAuthState((prev) => ({ ...prev, isLoading: false, loading: false }));
      return false;
    }
  };

  // 登出 - 调用后端接口
  const logout = async () => {
    try {
      // 调用后端登出接口
      await authService.logout();
    } catch (error) {
      // 即使后端调用失败，也要清除本地状态
    }

    // 清除本地存储
    localStorage.removeItem("mslogin_token");
    localStorage.removeItem("mslogin_refresh_token");
    localStorage.removeItem("mslogin_user");

    setAuthState({
      isAuthenticated: false,
      isLoading: false,
      loading: false,
      user: null,
      permissions: [],
      token: null,
    });

    message.success("已退出登录");
  };

  // 刷新Token
  const refreshToken = async (): Promise<boolean> => {
    try {
      const refreshTokenValue = localStorage.getItem("mslogin_refresh_token");
      if (!refreshTokenValue) {
        return false;
      }

      const result = await authService.refreshToken(refreshTokenValue);
      if (result.success && result.data) {
        const { user, accessToken } = result.data;

        localStorage.setItem("mslogin_token", accessToken);
        const refreshedUser = mapToAuthUser({
          id: user.id,
          account: user.account,
          username: user.username,
          description: user.description,
          roles: user.roles,
          roleDetails: user.roleDetails,
          rolePermissions: user.rolePermissions,
          permissions: user.permissions,
          isSuperAdmin: user.isSuperAdmin,
        });
        persistMsloginUser(refreshedUser);

        setAuthState((prev) => ({
          ...prev,
          isAuthenticated: true,
          isLoading: false,
          loading: false,
          user: refreshedUser,
          permissions: user.permissions || [],
          token: accessToken,
        }));

        // 触发权限变化事件
        window.dispatchEvent(new CustomEvent("userPermissionsChanged"));

        return true;
      } else {
        // 刷新失败，执行登出
        logout();
        return false;
      }
    } catch (error) {
      logout();
      return false;
    }
  };

  const hasWildcardPermission = (): boolean =>
    authState.permissions.includes("*");

  // 检查单个权限
  const checkPermission = (permission: string): boolean => {
    if (hasWildcardPermission()) return true;
    return authState.permissions.includes(permission);
  };

  // 检查是否有任意一个权限
  const hasAnyPermission = (permissions: string[]): boolean => {
    if (hasWildcardPermission()) return true;
    return permissions.some((permission) =>
      authState.permissions.includes(permission)
    );
  };

  // 检查是否有所有权限
  const hasAllPermissions = (permissions: string[]): boolean => {
    if (hasWildcardPermission()) return true;
    return permissions.every((permission) =>
      authState.permissions.includes(permission)
    );
  };

  const contextValue: AuthContextType = {
    ...authState,
    login,
    loginWithWecomCode,
    logout,
    refreshToken,
    checkPermission,
    hasAnyPermission,
    hasAllPermissions,
  };

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
